import { Button, Input, Modal } from '@bennows/boows-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFailure } from '../../lib/api';
import { passkeyErrorKind } from '../../lib/webauthn';
import { useToast } from '../../lib/toasts';
import { usePasskeyWrites } from './usePasskeys';

const FORM_ID = 'add-passkey';
/** As the API (`CreatePasskeyDto.name`). */
const NAME_MAX = 60;

/** The message key for a failed add, or `null`. */
function addFailureKey(error: unknown): string | null {
  if (!error) return null;
  if (!(error instanceof ApiFailure)) return `passkeys.errors.${passkeyErrorKind(error)}`;
  if (error.slug === 'passkey-already-registered') return 'passkeys.errors.duplicate';
  if (error.slug === 'passkey-challenge-expired') return 'passkeys.errors.expired';
  if (error.slug === 'network') return 'common.errorNetwork';
  return 'passkeys.errors.failed';
}

/** Names the new passkey, then hands over to the browser dialog (T460-1-4). */
export function AddPasskeyDialog({ onClose }: { readonly onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { add } = usePasskeyWrites();
  const [name, setName] = useState(t('passkeys.addDialog.defaultName'));
  const [submitted, setSubmitted] = useState(false);
  const nameMissing = name.trim() === '';

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (nameMissing) return;
    add.mutate(name.trim(), { onSuccess: (passkey) => (toast(t('passkeys.added', { name: passkey.name })), onClose()) });
  };
  const failure = add.isPending ? null : addFailureKey(add.error);

  return (
    <Modal
      open
      onClose={onClose}
      title={t('passkeys.addDialog.title')}
      description={t('passkeys.addDialog.description')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} loading={add.isPending}>
            {t('passkeys.addDialog.submit')}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="ui-console-form" onSubmit={onSubmit} noValidate>
        <Input
          label={t('passkeys.addDialog.name')}
          value={name}
          onChange={setName}
          maxLength={NAME_MAX}
          required
          invalid={submitted && nameMissing}
          help={submitted && nameMissing ? t('passkeys.addDialog.nameMissing') : t('passkeys.addDialog.nameHelp')}
        />
        {failure ? (
          <p className="ui-auth__message" role="alert">
            {t(failure)}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
